import { computed, ComputedRef, MaybeRefOrGetter, ref, toValue } from "vue";
import { DSInputToken, DSTokenInputEmits } from "@/src/design_system/form_elements/token_input/types";

interface UseTokenInputCaretOptions {
  tokens: MaybeRefOrGetter<DSInputToken[]>;
  caret: MaybeRefOrGetter<number | undefined>;
  emit: DSTokenInputEmits;
}

export interface TokenInputCaretApi {
  caret: ComputedRef<number>;
  selectedIndex: ComputedRef<number | null>;
  select: (index: number | null) => void;
  moveTo: (index: number) => void;
  remove: (index: number) => void;
  edit: (index: number) => void;
}

const clamp = (value: number, max: number): number => Math.min(Math.max(value, 0), max);

export const useTokenInputCaret = ({ tokens, caret, emit }: UseTokenInputCaretOptions): TokenInputCaretApi => {
  const selected = ref<number | null>(null);

  const clampedCaret = computed(() => {
    const count = toValue(tokens).length;

    return clamp(toValue(caret) ?? count, count);
  });

  const selectedIndex = computed(() =>
    selected.value !== null && selected.value < toValue(tokens).length ? selected.value : null
  );

  const select = (index: number | null): void => {
    const token = index === null ? undefined : toValue(tokens)[index];
    const next = token ? index : null;

    if (next === selectedIndex.value) {
      return;
    }

    selected.value = next;
    emit("token:select", token && next !== null ? { token, index: next } : null);
  };

  const moveTo = (index: number): void => {
    emit("update:caret", clamp(index, toValue(tokens).length));
  };

  const withoutToken = (index: number): DSInputToken[] => toValue(tokens).filter((_, position) => position !== index);

  const remove = (index: number): void => {
    const token = toValue(tokens)[index];

    if (!token) {
      return;
    }

    emit("update:caret", index);
    emit("update:tokens", withoutToken(index));
    emit("token:remove", { token, index });
    select(null);
  };

  const edit = (index: number): void => {
    const token = toValue(tokens)[index];

    if (!token) {
      return;
    }

    emit("update:caret", index);
    emit("update:modelValue", token.text);
    emit("update:tokens", withoutToken(index));
    emit("token:edit", { token, index });
    select(null);
  };

  return { caret: clampedCaret, selectedIndex, select, moveTo, remove, edit };
};
